import * as React from 'react';

import { useQuery } from '@apollo/client';

import { getCmsLocaleString } from 'js/lib/language';
import { useLocation, useParams } from 'js/lib/useRouter';

import { NotFound } from 'bundles/common/components/withNotFound';
import type {
  UnifiedRolePageCareerContentQuery,
  UnifiedRolePageCareerContentQueryVariables,
  UnifiedRolePageTemplateContentQuery,
  UnifiedRolePageTemplateContentQueryVariables,
} from 'bundles/unified-career-academy/__generated__/contentfulTypes';
import RolePageContentContext from 'bundles/unified-career-academy/contexts/RolePageContentContext';
import EventingDataProvider from 'bundles/unified-career-academy/providers/EventingDataProvider';
import {
  unifiedRolePageCareerContentQuery,
  unifiedRolePageTemplateQuery,
} from 'bundles/unified-career-academy/queries/contentfulQueries';

type Props = {
  children: React.ReactNode;
};

const ConsumerRolePageDataProvider = ({ children }: Props) => {
  const { roleSlug } = useParams();
  const { query } = useLocation();
  const previewParam = query.preview;
  const isPreview = Number(previewParam) === 1;
  const { data: rolePageCareerContentData, loading: rolePageCareerContentLoading } = useQuery<
    UnifiedRolePageCareerContentQuery,
    UnifiedRolePageCareerContentQueryVariables
  >(unifiedRolePageCareerContentQuery, {
    variables: {
      // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      slug: roleSlug,
      locale: getCmsLocaleString(),
      preview: isPreview,
    },
    context: { clientName: isPreview ? 'contentfulPreviewGql' : 'contentfulGql' },
  });

  const { data: rolePageTemplateContentData } = useQuery<
    UnifiedRolePageTemplateContentQuery,
    UnifiedRolePageTemplateContentQueryVariables
  >(unifiedRolePageTemplateQuery, {
    variables: {
      slug: 'consumer',
      locale: getCmsLocaleString(),
      preview: isPreview,
    },
    context: { clientName: isPreview ? 'contentfulPreviewGql' : 'contentfulGql' },
  });
  const rolePageCareerContent = rolePageCareerContentData?.unifiedCareerAcademyCareerCardCollection?.items?.[0];
  const rolePageTemplateContent = rolePageTemplateContentData?.unifiedCareerAcademyRdpTemplateCollection?.items?.[0];

  if (!rolePageCareerContent && !rolePageCareerContentLoading) {
    return <NotFound />;
  }

  return (
    <RolePageContentContext.Provider
      value={{
        template: rolePageTemplateContent,
        career: rolePageCareerContent,
      }}
    >
      {/*
       // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess */}
      <EventingDataProvider roleSlug={roleSlug}>{children}</EventingDataProvider>
    </RolePageContentContext.Provider>
  );
};

export default ConsumerRolePageDataProvider;
